// game/hud.ts — the run HUD, drawn on canvas in logical screen space.
// Everything the Warden must know without looking away from the fight.

import type { BossLike } from '../types'
import { TUNING } from '../data/tuning'
import { RELIC_RARITY_COLOR } from '../data/relics'
import { drawSprite } from '../engine/sprites'
import { heldRelics } from './relics'
import type { Run } from './game'

/**
 * The HUD asks the boss what to call it and where its phases sit. BossLike (a
 * frozen interface) carries neither, so both are read structurally: a boss that
 * declares `name` / `phaseTicks` gets its own, anything else gets a readable
 * fallback. No shared registry, nothing for a zone worker to remember.
 */
function bossName(b: BossLike): string {
  const n = (b as { name?: unknown }).name
  return typeof n === 'string' && n.length > 0 ? n : b.id.toUpperCase()
}

const DEFAULT_PHASE_TICKS = [0.25, 0.6]

function bossPhaseTicks(b: BossLike): readonly number[] {
  const t = (b as { phaseTicks?: unknown }).phaseTicks
  return Array.isArray(t) && t.every((v) => typeof v === 'number') ? (t as number[]) : DEFAULT_PHASE_TICKS
}

const PANEL = 'rgba(8,9,14,0.72)'
const GOLD = '#c9a227'
const INK = '#e8dcbf'

function panel(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
  ctx.fillStyle = PANEL
  ctx.fillRect(x, y, w, h)
  ctx.strokeStyle = 'rgba(201,162,39,0.35)'
  ctx.lineWidth = 1
  ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1)
}

function label(
  ctx: CanvasRenderingContext2D,
  s: string,
  x: number,
  y: number,
  size: number,
  color: string,
  align: CanvasTextAlign = 'left',
): void {
  ctx.font = `700 ${size}px ui-monospace, "SF Mono", Menlo, monospace`
  ctx.textAlign = align
  ctx.textBaseline = 'middle'
  ctx.fillStyle = '#000000'
  ctx.fillText(s, x + 1, y + 1)
  ctx.fillStyle = color
  ctx.fillText(s, x, y)
}

export function renderHud(ctx: CanvasRenderingContext2D, run: Run, w: number, _h: number): void {
  const p = run.player

  // ---------------------------------------------------------------- health
  const hx = 20
  const hy = 18
  const hw = 268
  const hh = 20
  panel(ctx, hx - 4, hy - 4, hw + 8, hh + 8)
  ctx.fillStyle = '#2a1216'
  ctx.fillRect(hx, hy, hw, hh)
  const frac = Math.max(0, p.hp / p.maxHp)
  const grad = ctx.createLinearGradient(hx, 0, hx + hw, 0)
  grad.addColorStop(0, frac < 0.3 ? '#8a1d1d' : '#a8341f')
  grad.addColorStop(1, frac < 0.3 ? '#d84a3a' : '#e8703c')
  ctx.fillStyle = grad
  ctx.fillRect(hx, hy, hw * frac, hh)
  // segment ticks every 25 HP
  ctx.fillStyle = 'rgba(0,0,0,0.45)'
  for (let v = 25; v < p.maxHp; v += 25) {
    ctx.fillRect(hx + Math.round((v / p.maxHp) * hw), hy, 1, hh)
  }
  label(ctx, `${Math.ceil(p.hp)} / ${p.maxHp}`, hx + hw / 2, hy + hh / 2, 13, INK, 'center')

  // ------------------------------------------------------------ dash pips
  const dy = hy + hh + 12
  for (let i = 0; i < p.maxDashCharges; i++) {
    const cx = hx + 7 + i * 20
    const filled = i < p.dashCharges
    ctx.beginPath()
    ctx.arc(cx, dy, 6, 0, Math.PI * 2)
    ctx.fillStyle = filled ? '#7fc9ff' : 'rgba(127,201,255,0.18)'
    ctx.fill()
    ctx.strokeStyle = 'rgba(127,201,255,0.6)'
    ctx.lineWidth = 1
    ctx.stroke()
  }
  if (p.dashCharges < p.maxDashCharges) {
    let best: number = TUNING.player.dashRecharge
    for (const c of p.dashCooldowns) if (c > 0 && c < best) best = c
    const k = 1 - best / TUNING.player.dashRecharge
    ctx.fillStyle = 'rgba(127,201,255,0.55)'
    ctx.fillRect(hx + 1, dy + 9, 44 * k, 2)
  }
  label(ctx, 'DASH', hx + 7 + p.maxDashCharges * 20 + 6, dy, 10, 'rgba(200,220,240,0.65)')

  // ---------------------------------------------------------------- gloom
  const gw = 320
  const gx = w / 2 - gw / 2
  const gy = 20
  panel(ctx, gx - 4, gy - 4, gw + 8, 20)
  ctx.fillStyle = '#120f1c'
  ctx.fillRect(gx, gy, gw, 12)
  const gv = run.gloomSys.value
  const gg = ctx.createLinearGradient(gx, 0, gx + gw, 0)
  gg.addColorStop(0, '#3a2a5c')
  gg.addColorStop(0.6, '#6b3fa0')
  gg.addColorStop(1, '#b64dd8')
  ctx.fillStyle = gg
  ctx.fillRect(gx, gy, gw * gv, 12)
  ctx.fillStyle = 'rgba(255,255,255,0.28)'
  ctx.fillRect(gx + gw * TUNING.gloom.restlessAt, gy - 2, 1, 16)
  ctx.fillRect(gx + gw * TUNING.gloom.seethingAt, gy - 2, 1, 16)
  const state = run.gloomSys.arrived
    ? 'THE GLOOM'
    : run.gloomSys.seething
      ? 'SEETHING'
      : run.gloomSys.restless
        ? 'RESTLESS'
        : 'GLOOM'
  label(
    ctx,
    state,
    w / 2,
    gy + 24,
    11,
    run.gloomSys.arrived ? '#e07bff' : run.gloomSys.seething ? '#c78bff' : 'rgba(200,180,220,0.7)',
    'center',
  )

  // ---------------------------------------- gold / embers / potions / keys
  // Everything the run is worth, captioned so a first run needs no manual:
  // gold (in-run, lost on death) · embers (banked, kept on death) · potion + Q.
  const rx = w - 20
  panel(ctx, rx - 264, 14, 264, 46)
  drawSprite(ctx, 'pickup_gold', rx - 240, 30, { scale: 1.1 })
  label(ctx, String(p.gold), rx - 224, 30, 15, GOLD)
  // embers are light, not an object — the HUD draws the same mote the floor does
  const ex = rx - 156
  ctx.fillStyle = 'rgba(255,154,60,0.3)'
  ctx.beginPath()
  ctx.arc(ex, 30, 8, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#ffe9a8'
  ctx.beginPath()
  ctx.arc(ex, 30, 3.5, 0, Math.PI * 2)
  ctx.fill()
  label(ctx, String(run.embers), ex + 14, 30, 15, '#ffb347')
  drawSprite(ctx, 'pickup_potion', rx - 72, 30, { scale: 1.1 })
  label(ctx, `${p.potions}/${p.potionCap}`, rx - 56, 30, 15, p.potions > 0 ? '#ff9a5c' : 'rgba(255,154,92,0.35)')
  const CAPTION = 'rgba(232,220,191,0.45)'
  label(ctx, 'GOLD', rx - 248, 50, 8, CAPTION)
  label(ctx, 'EMBERS · KEPT', ex - 8, 50, 8, CAPTION)
  label(ctx, 'POTION · Q', rx - 80, 50, 8, CAPTION)
  let ry = 68
  if (p.keys > 0) {
    panel(ctx, rx - 264, ry, 60, 28)
    drawSprite(ctx, 'pickup_key', rx - 248, ry + 14, { scale: 1 })
    label(ctx, String(p.keys), rx - 232, ry + 14, 14, '#e6d68a')
    ry += 36
  }

  // ------------------------------------------------------------ relics held
  // Right edge, smallest readable size: what you picked up is never a mystery
  // mid-run (Esc shows the same list with full descriptions).
  const relics = heldRelics()
  if (relics.length > 0) {
    label(ctx, 'RELICS', rx, ry + 4, 8, CAPTION, 'right')
    const maxRows = 12
    const n = Math.min(relics.length, relics.length > maxRows ? maxRows - 1 : maxRows)
    for (let i = 0; i < n; i++) {
      label(ctx, relics[i].name, rx, ry + 18 + i * 13, 10, RELIC_RARITY_COLOR[relics[i].rarity], 'right')
    }
    if (relics.length > n) {
      label(ctx, `+${relics.length - n} more`, rx, ry + 18 + n * 13, 10, CAPTION, 'right')
    }
  }

  // ------------------------------------------------------- special cooldown
  const sx = hx
  const sy = dy + 20
  const specialReady = p.specialCd <= 0
  label(ctx, 'RMB', sx, sy + 7, 10, 'rgba(200,220,240,0.6)')
  ctx.fillStyle = 'rgba(255,255,255,0.12)'
  ctx.fillRect(sx + 30, sy + 2, 92, 9)
  ctx.fillStyle = specialReady ? '#ffb347' : 'rgba(255,179,71,0.45)'
  const specMax = Math.max(0.001, run.specialCooldownMax)
  const k = specialReady ? 1 : 1 - p.specialCd / specMax
  ctx.fillRect(sx + 30, sy + 2, 92 * Math.max(0, Math.min(1, k)), 9)
  if (specialReady) label(ctx, 'READY', sx + 128, sy + 7, 10, '#ffb347')
  label(ctx, 'ESC — MENU / HELP', sx, sy + 24, 9, 'rgba(200,220,240,0.4)')

  // --------------------------------------------------------------- boss bar
  // Bottom centre, out of the way of the fight but impossible to miss: the name,
  // the Heart's health, and a tick at each phase threshold so the player can see
  // how far the next change of behaviour is.
  const boss = run.boss
  if (boss && boss.maxHp > 0) {
    const bw = 520
    const bx = w / 2 - bw / 2
    // hugs the bottom edge; barks move to the top while a boss is live
    const by = 668
    panel(ctx, bx - 6, by - 20, bw + 12, 42)
    label(ctx, bossName(boss), w / 2, by - 9, 13, '#ffd98a', 'center')
    ctx.fillStyle = '#25151a'
    ctx.fillRect(bx, by, bw, 12)
    const bf = Math.max(0, Math.min(1, boss.hp / boss.maxHp))
    const bg = ctx.createLinearGradient(bx, 0, bx + bw, 0)
    bg.addColorStop(0, '#7a1d1d')
    bg.addColorStop(1, '#e0523c')
    ctx.fillStyle = bg
    ctx.fillRect(bx, by, bw * bf, 12)
    ctx.fillStyle = 'rgba(0,0,0,0.6)'
    for (const t of bossPhaseTicks(boss)) ctx.fillRect(bx + Math.round(bw * t), by - 2, 2, 16)
    ctx.strokeStyle = 'rgba(255,217,138,0.4)'
    ctx.lineWidth = 1
    ctx.strokeRect(bx + 0.5, by + 0.5, bw - 1, 11)
    label(ctx, `PHASE ${boss.phase}`, bx + bw + 2, by + 6, 11, '#ffb347', 'right')
  }

  // ------------------------------------------------------------- location
  const zoneName = run.zone.name.toUpperCase()
  label(ctx, zoneName, 20, 700, 13, 'rgba(232,220,191,0.8)')
  const spurs = run.zoneRunner.mode === 'spurs'
  const roomLabel = run.isBossRoom
    ? run.room.cleared
      ? 'BOSS ARENA — THE STAIR IS OPEN'
      : 'BOSS ARENA'
    : run.room.isRespite
      ? spurs
        ? `THE HEARTCHAMBER — SEALS ${run.zoneRunner.seals}/${run.zoneRunner.spurs.length}`
        : 'RESPITE'
      : spurs
        ? `SPUR ${run.room.index + 1} — SEALS ${run.zoneRunner.seals}/${run.zoneRunner.spurs.length}${run.room.cleared ? ' — RETURN' : ''}`
        : `ROOM ${run.room.index + 1} / ${run.zoneRunner.order.length}${run.room.cleared ? ' — CLEAR' : ''}`
  label(ctx, roomLabel, 20, 684, 11, run.room.cleared ? '#8ee87a' : 'rgba(232,220,191,0.55)')

  // ---------------------------------------------------------- run counters
  // (embers moved into the top-right wallet panel — they were invisible here)
  label(ctx, `KILLS ${run.kills}`, w - 20, 700, 11, 'rgba(232,220,191,0.55)', 'right')
}
